/**
 * AGROMX Frontend - Permissions Service
 * Servicio de permisos del usuario logueado.
 * Cachea los permisos al login y expone helpers para consultar.
 */

import api from './api.js';

class PermissionsService {
  constructor() {
    this._permisos = null; // Map<modulo_clave, Set<accion_clave>>
    this._esAdmin = false;
  }

  /**
   * Cargar permisos del usuario logueado desde el backend.
   * Llamar una vez al hacer login.
   */
  async load() {
    try {
      const response = await api.get('/auth/me/permisos');
      const grupos = response.data?.grupos || [];
      this._aplicarDatos(grupos);
    } catch (err) {
      console.error('Error cargando permisos:', err);
      this._permisos = new Map();
      this._grupos = [];
    }
  }

  _aplicarDatos(grupos) {
    this._grupos = grupos;
    this._permisos = new Map();
    for (const grupo of grupos) {
      for (const modulo of (grupo.modulos || [])) {
        this._permisos.set(modulo.clave, new Set(modulo.acciones || []));
      }
    }
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    this._esAdmin = user.rol === 'admin';
  }

  /**
   * Obtener grupos con módulos (para menú dinámico).
   * Usa datos ya cacheados, no hace otra llamada API.
   */
  get grupos() {
    return this._grupos || [];
  }

  /**
   * Verificar si el usuario tiene permiso para una acción en un módulo.
   * Admin siempre retorna true.
   *
   * @param {string} modulo - clave del módulo (ej. 'recepciones', 'ordenes')
   * @param {string} accion - clave de la acción (ej. 'ver', 'crear', 'editar', 'eliminar')
   * @returns {boolean}
   */
  can(modulo, accion) {
    if (this._esAdmin) return true;
    if (!this._permisos) return false;
    const acciones = this._permisos.get(modulo);
    return acciones ? acciones.has(accion) : false;
  }

  /**
   * Verificar si el usuario puede ver un módulo (atajo para can(modulo, 'ver'))
   */
  canView(modulo) {
    return this.can(modulo, 'ver');
  }

  /**
   * Verificar si el usuario puede crear en un módulo
   */
  canCreate(modulo) {
    return this.can(modulo, 'crear');
  }

  /**
   * Verificar si el usuario puede editar en un módulo
   */
  canEdit(modulo) {
    return this.can(modulo, 'editar');
  }

  /**
   * Verificar si el usuario puede eliminar en un módulo
   */
  canDelete(modulo) {
    return this.can(modulo, 'eliminar');
  }

  /**
   * Verificar si el usuario puede imprimir en un módulo
   */
  canPrint(modulo) {
    return this.can(modulo, 'imprimir');
  }

  /**
   * Si el usuario es admin (acceso total).
   */
  get esAdmin() {
    return !!this._esAdmin;
  }

  /**
   * Marcar como admin. Conserva los grupos si ya estaban cargados (para
   * que el sidebar dinámico pueda mostrar los módulos del catálogo).
   */
  setAdmin() {
    this._esAdmin = true;
    this._permisos = new Map();
    // No resetear _grupos: si ya se cargaron desde BD, los conservamos.
    if (!this._grupos) this._grupos = [];
  }

  /**
   * Limpiar permisos (al hacer logout)
   */
  clear() {
    this._permisos = null;
    this._esAdmin = false;
    this._grupos = [];
  }

  /**
   * Verificar si los permisos ya están cargados
   */
  get loaded() {
    return this._permisos !== null;
  }

  /**
   * Obtener la primera ruta disponible del usuario
   */
  get primeraRuta() {
    if (this._esAdmin) return '/dashboard';
    for (const grupo of (this._grupos || [])) {
      for (const modulo of (grupo.modulos || [])) {
        if (modulo.ruta) return modulo.ruta;
      }
    }
    return null;
  }
}

export default new PermissionsService();
